import React, { useEffect, useMemo, useState } from "react";

const ModalWrapper = ({ open, title, children, onClose }) => {
  if (!open) return null;
  return (
    <div
      className="modal fade show d-block management-modal"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
    >
      <div className="modal-dialog modal-dialog-centered" role="document">
        <div className="modal-content">
          <div className="modal-header border-0">
            <h5 className="modal-title">{title}</h5>
            <button type="button" className="btn-close" aria-label="Cerrar" onClick={onClose} />
          </div>
          {children}
        </div>
      </div>
    </div>
  );
};

export default function CargoFormModal({ open, onClose, onSubmit, conceptos = [], submitting = false, matriculaPrefill = null }) {
  const [form, setForm] = useState({ concepto_id: "", concepto_nombre: "", monto: "", observaciones: "", matricula_search: "" });
  const [matriculasOptions, setMatriculasOptions] = useState([]);
  const [loadingMatriculas, setLoadingMatriculas] = useState(false);
  const [conceptoSearch, setConceptoSearch] = useState("");
  const [conceptoMenuOpen, setConceptoMenuOpen] = useState(false);

  const conceptoSeleccionado = conceptos.find((c) => String(c.id) === String(form.concepto_id));
  const conceptoInputValue = conceptoSearch || conceptoSeleccionado?.nombre || "";

  const conceptoSugerencias = useMemo(() => {
    const term = conceptoSearch.trim().toLowerCase();
    const lista = conceptos.filter((c) => !c.afecta_matricula);
    if (!term) return lista;
    return lista.filter((c) => c.nombre.toLowerCase().includes(term));
  }, [conceptos, conceptoSearch]);

  const onChange = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    if (!open) {
      setForm({ concepto_id: "", concepto_nombre: "", monto: "", observaciones: "", matricula_search: "" });
      setConceptoSearch("");
      setConceptoMenuOpen(false);
      setMatriculasOptions([]);
    } else if (matriculaPrefill) {
      setForm((prev) => ({
        ...prev,
        matricula_id: String(matriculaPrefill.matricula_id),
        estudiante_id: String(matriculaPrefill.estudiante_id || ""),
      }));
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Seleccionar primer concepto adicional por defecto
  useEffect(() => {
    if (open && conceptos.length && !form.concepto_id) {
      const primero = conceptos.find((c) => !c.afecta_matricula);
      if (primero) onChange("concepto_id", String(primero.id));
    }
  }, [open, conceptos]); // eslint-disable-line react-hooks/exhaustive-deps

  const buscarMatriculas = async (search) => {
    if (!search || search.trim().length < 3) return;
    setLoadingMatriculas(true);
    try {
      const { buscarMatriculas: svc } = await import("../../../../services/matriculasService");
      const data = await svc({ search: search.trim(), limit: 20 });
      const opts = (Array.isArray(data) ? data : []).map((item) => {
        const nombre = [item.estudiante_nombres, item.estudiante_apellidos].filter(Boolean).join(" ");
        const documento = item.estudiante_documento || item.documento_identidad || "";
        const codigo = item.codigo_matricula || "";
        return {
          value: item.id,
          label: nombre,
          documento,
          codigo,
          estudianteId: item.estudiante_id,
        };
      });
      setMatriculasOptions(opts);
    } catch {
      setMatriculasOptions([]);
    } finally {
      setLoadingMatriculas(false);
    }
  };

  const selectedMatricula = matriculasOptions.find((o) => String(o.value) === String(form.matricula_id));
  const showResults = form.matricula_search && !form.matricula_id && matriculasOptions.length > 0;

  const handleSubmit = () => {
    if (!form.matricula_id) return;
    const payload = {
      matricula_id: form.matricula_id,
      monto: parseFloat(form.monto),
      observaciones: form.observaciones || undefined,
    };
    if (form.concepto_id) {
      payload.concepto_id = Number(form.concepto_id);
    } else {
      payload.concepto_nombre = form.concepto_nombre || "Cargo adicional";
    }
    onSubmit(payload);
  };

  return (
    <ModalWrapper open={open} title="Registrar cargo adicional" onClose={onClose}>
      <div className="modal-body">
        <div className="row g-3">
          {/* Búsqueda de estudiante */}
          <div className="col-12">
            <label className="form-label">Estudiante / Matrícula</label>
            {matriculaPrefill ? (
              <div className="form-control bg-light">
                <strong>{matriculaPrefill.estudiante}</strong>
                <span className="text-muted ms-2 small">{matriculaPrefill.documento} · {matriculaPrefill.codigo}</span>
              </div>
            ) : selectedMatricula ? (
              <div className="d-flex align-items-center gap-2">
                <div className="flex-grow-1 form-control form-control-sm bg-light">
                  <strong>{selectedMatricula.label}</strong>
                  <span className="text-muted ms-2 small">{selectedMatricula.documento} · {selectedMatricula.codigo}</span>
                </div>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => { onChange("matricula_id", ""); setMatriculasOptions([]); onChange("matricula_search", ""); }}
                >
                  <i className="bi bi-x" />
                </button>
              </div>
            ) : (
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Buscar por cédula, nombre o código"
                  value={form.matricula_search || ""}
                  onChange={(e) => onChange("matricula_search", e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); buscarMatriculas(form.matricula_search); } }}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => buscarMatriculas(form.matricula_search)}
                  disabled={loadingMatriculas}
                >
                  {loadingMatriculas ? <i className="bi bi-arrow-repeat spin" /> : <i className="bi bi-search" />}
                </button>
              </div>
            ) /* end selectedMatricula ternary */}
            {!matriculaPrefill && showResults && (
              <div className="list-group small mt-1" style={{ maxHeight: 180, overflowY: "auto" }}>
                {matriculasOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className="list-group-item list-group-item-action d-flex justify-content-between"
                    onClick={() => { onChange("matricula_id", String(opt.value)); onChange("estudiante_id", String(opt.estudianteId || "")); }}
                  >
                    <span><strong>{opt.label}</strong> <span className="text-muted">{opt.documento}</span></span>
                    <small className="text-muted">{opt.codigo}</small>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Concepto */}
          <div className="col-md-6">
            <label className="form-label">Concepto</label>
            <div className="position-relative">
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Ej: Camisa, libro…"
                  value={conceptoInputValue}
                  onFocus={() => setConceptoMenuOpen(true)}
                  onBlur={() => setTimeout(() => setConceptoMenuOpen(false), 150)}
                  onChange={(e) => {
                    setConceptoSearch(e.target.value);
                    setConceptoMenuOpen(true);
                    onChange("concepto_id", "");
                    onChange("concepto_nombre", e.target.value);
                  }}
                />
                {conceptoInputValue && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => { setConceptoSearch(""); onChange("concepto_id", ""); onChange("concepto_nombre", ""); }}
                  >
                    <i className="bi bi-x-lg" />
                  </button>
                )}
              </div>
              {conceptoMenuOpen && (
                <div className="list-group shadow-sm position-absolute w-100" style={{ zIndex: 1050, top: "100%" }}>
                  {conceptoSugerencias.length > 0 ? (
                    conceptoSugerencias.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className={`list-group-item list-group-item-action${String(c.id) === String(form.concepto_id) ? " active" : ""}`}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => { onChange("concepto_id", String(c.id)); onChange("concepto_nombre", ""); setConceptoSearch(""); setConceptoMenuOpen(false); }}
                      >
                        {c.nombre}
                      </button>
                    ))
                  ) : (
                    <div className="list-group-item text-muted small">
                      {conceptoSearch.trim() ? `Se creará "${conceptoSearch.trim()}" al guardar.` : "Sin coincidencias."}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Monto */}
          <div className="col-md-6">
            <label className="form-label">Monto</label>
            <input
              type="number"
              step="0.01"
              className="form-control"
              placeholder="0.00"
              value={form.monto}
              onChange={(e) => onChange("monto", e.target.value)}
            />
          </div>

          {/* Observaciones */}
          <div className="col-12">
            <label className="form-label">Observaciones <span className="text-muted">(opcional)</span></label>
            <textarea
              className="form-control"
              rows={2}
              placeholder="Talla, color, descripción…"
              value={form.observaciones}
              onChange={(e) => onChange("observaciones", e.target.value)}
            />
          </div>
        </div>
      </div>
      <div className="modal-footer border-0">
        <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={submitting}>
          Cancelar
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleSubmit}
          disabled={submitting || !form.matricula_id || !form.monto || parseFloat(form.monto) <= 0}
        >
          {submitting ? (
            <><i className="bi bi-arrow-repeat me-2 spin" />Guardando...</>
          ) : (
            <><i className="bi bi-plus-circle me-2" />Registrar cargo</>
          )}
        </button>
      </div>
    </ModalWrapper>
  );
}
